import { useCallback } from "react";

import toast from "react-hot-toast";
import { Link } from "react-router-dom";

import { UsersIcon } from "@lucide/icons";

import { Button, ButtonLink } from "simplydo/react/ui/button";
import { Card, CardContent, CardDescription } from "simplydo/react/ui/card";
import { H2, H3 } from "simplydo/react/ui/header";
import { Icon } from "simplydo/react/ui/icon";
import { Message } from "simplydo/react/ui/message";

import { useAppSelector } from "store";
import { useUserReload } from "utils/useUserReload";
import util from "utils/utils";

import { UserChip } from "components/lib/Chips";

import InviteeChip from "./InviteeChip";
import type { InviteCardProps } from "./types";

const GroupInviteCard = ({
  invitation,
  acceptInvite,
  rejectInvite,
  removeInvite,
  cardView = true,
}: InviteCardProps) => {
  const reloadUser = useUserReload();
  const user = useAppSelector((state) => state.user);
  const { inviterUser, group, createdByCurrentUser } = invitation;

  const accept = useCallback(() => {
    util
      .confirm("Join group", `Are you sure you want to join "${group?.name}"?`)
      .then(() => {
        acceptInvite(
          invitation,
          () => {
            reloadUser();
            toast.success("Joined group");
          },
          () => toast.error("Error joining group"),
        );
      })
      .catch(() => {});
  }, [acceptInvite, invitation, group?.name, reloadUser]);

  const reject = useCallback(() => {
    util
      .confirm(
        "Reject group invitation",
        `Are you sure you want to reject the invitation to join "${group?.name || "Deleted group"}"?`,
      )
      .then(() => {
        rejectInvite(
          invitation,
          () => {
            toast("Invitation rejected");
          },
          () => toast.error("Error rejecting invite"),
        );
      })
      .catch(() => {});
  }, [rejectInvite, invitation, group?.name]);

  const remove = useCallback(() => {
    util
      .confirm(
        "Remove group invitation",
        `Are you sure you want to remove this invitation to join "${group?.name || "Deleted group"}"?`,
      )
      .then(() => {
        removeInvite(
          invitation,
          () => {
            toast("Invitation removed");
          },
          () => toast.error("Error removing invite"),
        );
      })
      .catch(() => {});
  }, [removeInvite, invitation, group?.name]);

  const canAccept = invitation.canAccept && !!group;

  return (
    <div
      style={
        cardView
          ? {
              display: "flex",
              justifyContent: "space-between",
              alignItems: "center",
            }
          : {}
      }
    >
      <div>
        {cardView ? (
          <H3 style={{ marginBottom: "0.3em" }}>
            <Link to={group ? `/groups/${group._id}` : ""}>
              {group?.name || "Deleted group"}
            </Link>
          </H3>
        ) : (
          <H2 style={{ textAlign: "center" }}>
            <Icon icon={UsersIcon} />
            Join {group?.name || `Deleted group`}
          </H2>
        )}

        <CardDescription>
          {createdByCurrentUser ? (
            <InviteeChip
              invitation={invitation}
              preText="You invited"
              postText="to join this group"
            />
          ) : (
            <UserChip
              compact="very"
              user={inviterUser}
              postText="has invited you to join this group"
            />
          )}
        </CardDescription>
      </div>
      <CardContent>
        <div
          style={
            cardView
              ? { display: "flex", justifyContent: "flex-end" }
              : { marginTop: 20, display: "flex", justifyContent: "flex-end" }
          }
        >
          {invitation.acceptedAt && group && (
            <ButtonLink
              color="green"
              variant="outline"
              to={`/groups/${group?._id}`}
            >
              Go to group
            </ButtonLink>
          )}
          {!invitation.acceptedAt && (
            <>
              {!group && (
                <Message color="red" style={{ width: "100%" }}>
                  This invitation to join is no longer valid because the group
                  has been deleted.
                </Message>
              )}
              {user && invitation.canRemove ? (
                <Button
                  color={createdByCurrentUser ? undefined : "red"}
                  onClick={createdByCurrentUser ? remove : reject}
                  variant="outline"
                >
                  {createdByCurrentUser ? "Remove" : "Reject"}
                </Button>
              ) : null}
              {user && canAccept ? (
                <Button color="green" onClick={accept} variant="outline">
                  Accept
                </Button>
              ) : null}
            </>
          )}
        </div>
      </CardContent>
    </div>
  );
};

export default GroupInviteCard;
